import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import {
    GablePendingQuestions,
    GableQuestion,
} from './gable-stream.service';
import { PageContextService } from './page-context.service';

/**
 * Citation returned by the assistant orchestrator — points at a row in one
 * of the v_* views (or the underlying base table) so the UI can later render
 * a deep link.
 */
export interface GableCitation {
    table: string;
    id: number;
}

/**
 * A pending write proposal awaiting human approval. Created by the AI via
 * `GenericModelCrudTool` in propose mode; committed (or rejected) via the
 * approve/reject endpoints.
 */
/**
 * Hardcoded allowlist of model class names. Mirrors
 * GenericModelCrudTool::ALLOWED_MODELS on the backend. Gable pins this so
 * any drift between FE and BE is a TS compile error; Phase 1 swaps this for
 * a server-driven capability registry where the FE doesn't need a literal
 * union at all.
 */
export type GableModelClass = 'Unit' | 'Vendor' | 'ViolationStatement';

export interface GablePendingProposal {
    id: number;
    /** Eloquent short name for legacy CRUD rows, or `action:<type>` for typed actions. */
    modelClass: GableModelClass | string;
    /** Typed-action rows only (e.g. 'approve_payable'); null for legacy CRUD. */
    actionType: string | null;
    operation: 'create' | 'update' | 'delete';
    entityId: number | null;
    payload: Record<string, unknown>;
    /** Persisted section bundle for typed-action rows — renderable via app-gable-action-preview-card. */
    preview: { sections: unknown[]; actions?: unknown } | null;
    summary: string;
    createdAt: string | null;
    expiresAt: string;
}

/** One decided proposal in the Activity feed (GET proposals/activity). */
export interface GableActivityItem {
    id: number;
    actionType: string | null;
    operation: 'create' | 'update' | 'delete';
    status: 'approved' | 'rejected' | 'expired';
    summary: string;
    createdAt: string | null;
    decidedAt: string | null;
    /** Commit-log result payload for approved rows; null otherwise. */
    result: Record<string, unknown> | null;
}

export interface GableActivityFeedResponse {
    items: GableActivityItem[];
    page: number;
    perPage: number;
    total: number;
    lastPage: number;
}

/** Per-action-type counts (GET proposals/activity-summary) — feeds the hero's module quick links. */
export interface GableActivitySummaryItem {
    actionType: string;
    pending: number;
    decided: number;
}

/**
 * Tools whose results render as inline tool-cards in the drawer. Single
 * source of truth — consumed by both the `GableToolCard.tool` type
 * union AND the Pusher-fallback heuristic in `GableDrawerService` so the
 * two can't drift.
 */
export const CARD_PRODUCING_TOOLS = ['generate_report', 'redirect_to_budget', 'propose_action', 'build_document'] as const;

export type CardProducingTool = typeof CARD_PRODUCING_TOOLS[number];

/**
 * A structured tool-result payload the UI renders inline. Currently
 * only emitted by `generate_report` — its payload carries the full
 * report data + signed download URLs + a deeplink to the dedicated
 * Reports page so the drawer can render the table via `<app-report-table>`
 * with the same fidelity as `/app/reports/*`.
 */
export interface GableToolCard {
    tool: CardProducingTool | string; // string fallback for future card kinds
    payload:
        | GenerateReportCardPayload
        | BudgetRedirectCardPayload
        | ActionPreviewCardPayload
        | DeliverableCardPayload
        | Record<string, unknown>;
}

/**
 * Section discriminated union for the action-preview card. Backend emits
 * snake_case; backend → UI middleware does NOT touch these JSON blobs
 * (they live inside a tool-card `payload` envelope), so keys here stay
 * snake_case to mirror the wire shape exactly. Section components do their
 * own narrowing on `kind`.
 */
export type ActionPreviewSection =
    | ActionPreviewHeaderSection
    | ActionPreviewStatusPickerSection
    | ActionPreviewEmailTemplateSection
    | ActionPreviewToggleSection
    | ActionPreviewRecipientsSection
    | ActionPreviewRecipientsPeopleSection
    | ActionPreviewImpactSummarySection
    | ActionPreviewKeyValueSection
    | ActionPreviewInfoBlockSection
    | ActionPreviewTableSection;

export interface ActionPreviewHeaderSection {
    kind: 'header';
    title: string;
    subtitle?: string;
    statusChip?: ActionPreviewChip;
}

export interface ActionPreviewEmailTemplateSection {
    kind: 'email_template';
    label: string;
    subject: string;
    body: string;
    footnote?: { icon?: string; text: string };
    editable: boolean;
}

/**
 * Status dropdown rendered as its own card section so actions other than
 * violations (charges, requests, etc.) can reuse it without burying the
 * value inside an email-template edit modal. Always-editable; the value
 * flows through `pendingEdits.status_picker.value` on the card host and
 * is posted back on commit-action.
 */
export interface ActionPreviewStatusPickerSection {
    kind: 'status_picker';
    label: string;
    value: string;
    options: ActionPreviewStatusOption[];
}

export interface ActionPreviewStatusOption {
    value: string;
    label: string;
    tone?: ActionPreviewChip['tone'];
}

/**
 * Boolean checkbox for optional side effects (e.g. "send email"). The
 * `key` field is the slot under `pendingEdits.toggles[key]` — each action
 * names its own keys, so one section type covers send_email / notify_board
 * / log_to_history without any new component work.
 */
export interface ActionPreviewToggleSection {
    kind: 'toggle';
    key: string;
    label: string;
    description?: string;
    value: boolean;
}

export interface ActionPreviewRecipientsSection {
    kind: 'recipients';
    label: string;
    countSuffix?: string;
    listActions?: { id: string; label: string }[];
    items: ActionPreviewRecipientItem[];
}

export interface ActionPreviewRecipientItem {
    id: string;
    primary: string;
    secondary?: string;
    removable?: boolean;
    removed?: boolean;
}

/**
 * People-picker variant of the recipients section — renders `app-people-picker`
 * (multi-select + free-text email) instead of the unit selector. Pre-selected
 * `items` use `person-{membershipId}` ids; the edit envelope (shared with the
 * unit variant) carries `person-{id}` and `email:{addr}` ids.
 */
export interface ActionPreviewRecipientsPeopleSection {
    kind: 'recipients_people';
    label: string;
    countSuffix?: string;
    note?: string;
    items: ActionPreviewRecipientItem[];
}

export interface ActionPreviewImpactSummarySection {
    kind: 'impact_summary';
    label: string;
    primary: string;
    secondary?: string;
    tone?: 'info' | 'success' | 'warning';
}

export interface ActionPreviewKeyValueSection {
    kind: 'key_value';
    label?: string;
    items: { label: string; value: string }[];
}

export interface ActionPreviewInfoBlockSection {
    kind: 'info_block';
    label?: string;
    body: string;
    tone?: 'info' | 'warning';
}

export interface ActionPreviewTableSection {
    kind: 'table';
    label?: string;
    columns: unknown[];
    rows: unknown[];
}

export interface ActionPreviewChip {
    label: string;
    tone?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'muted';
}

export interface ActionPreviewActions {
    destructive: { id: string; label: string };
    primary: { id: string; label: string; icon?: string };
}

export interface ActionPreviewCardPayload {
    /** Section-contract version (SD-7967). Absent on pre-versioning payloads — treat as 1. */
    schema_version?: number;
    proposal_id: number;
    action_type: string;
    preview: { sections: ActionPreviewSection[]; actions: ActionPreviewActions };
    state: 'pending' | 'committing' | 'committed' | 'cancelled';
}

export interface ActionPreviewCommitResponse {
    state: 'committed';
    proposalId: number;
    commitLogId: number;
    idempotent: boolean;
    result: Record<string, unknown>;
}

export interface ActionPreviewRefineResponse {
    proposalId: number;
    actionType: string;
    preview: { sections: ActionPreviewSection[]; actions: ActionPreviewActions };
    edits: ActionPreviewEdits;
}

/**
 * In-flight edit delta the UI accumulates on the card. Keyed by section
 * kind so each section emits only the shape it owns. Posted back to the
 * server on `refine` (utility action click) and on `commit-action` (primary
 * confirm). Server re-applies edits to params before validating + executing.
 */
export interface ActionPreviewEdits {
    email_template?: { subject?: string; body?: string };
    recipients?: { removed_ids?: string[]; added_ids?: string[] };
    status_picker?: { value: string };
    toggles?: Record<string, boolean>;
}

export interface BudgetRedirectCardPayload {
    label: string;        // e.g. "Go to budgets now"
    url: string;          // /app/budget-drafts or /app/budgets/detail
    is_draft: boolean;    // discriminator: drafts page vs live budget detail
    helper_text?: string; // short context line under the link
}

export interface GenerateReportCardPayload {
    report_key: string;
    report_name: string;
    org_name?: string;
    date_range?: string | null;
    generated_at?: string | null;
    reports_page_url?: string | null;
    downloads: {
        pdf: { url: string; file_name: string; expires_in_hours: number };
        csv: { url: string; file_name: string; expires_in_hours: number };
    };
    data: {
        columns: unknown[];
        rows: unknown[];
        summary?: Record<string, unknown>;
        subtitle?: string;
        extra?: Record<string, unknown>;
    };
    digest?: string;
    row_count?: number;
}

/**
 * Runtime narrowing guard for `generate_report` tool-card payloads.
 * Checks the minimal shape the drawer relies on (downloads + tabular data).
 */
export function isGenerateReportCardPayload(p: unknown): p is GenerateReportCardPayload {
    // Payload keys are emitted snake_case by core but the response middleware
    // camelCases them before they reach the UI — so we only assert on the
    // shape the drawer template actually relies on (tabular `data` + the
    // `downloads` object) and let casing differences pass through.
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    const data = obj['data'];
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    const dataObj = data as Record<string, unknown>;
    if (!Array.isArray(dataObj['columns']) || !Array.isArray(dataObj['rows'])) {
        return false;
    }
    const downloads = obj['downloads'];
    if (typeof downloads !== 'object' || downloads === null) {
        return false;
    }
    return true;
}

/**
 * `build_document` deliverable card — a generated document (e.g. board summary
 * .docx/.pdf) with token-authed 24h download links. Keys stay camelCase: tool-card
 * payload envelopes are NOT re-cased by the response middleware.
 */
export interface DeliverableCardFile {
    format: string; // 'docx' | 'pdf'
    fileName: string;
    sizeLabel: string;
    downloadUrl: string;
    previewable: boolean; // reserved for a future in-drawer Preview button (SD-7242); not rendered yet
}

export interface DeliverableCardPayload {
    title: string;
    files: DeliverableCardFile[];
    savedTo?: string;
    // Payload-only for now: per PO direction the card uses a single Download button +
    // format dropdown rather than "tools used" chips. Kept for telemetry / future use.
    toolsUsed?: string[];
}

/**
 * Runtime narrowing guard for `build_document` tool-card payloads. Requires a
 * non-empty `files` list where each entry has at least a `format` + `downloadUrl`
 * — otherwise the card could render an `<a href="undefined">`. A malformed/empty
 * payload is skipped via the warn-and-return-null path in `asDeliverableCardPayload`.
 */
export function isDeliverableCardPayload(p: unknown): p is DeliverableCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    if (typeof obj['title'] !== 'string' || !Array.isArray(obj['files']) || obj['files'].length === 0) {
        return false;
    }
    return (obj['files'] as unknown[]).every((file) => {
        if (typeof file !== 'object' || file === null) {
            return false;
        }
        const f = file as Record<string, unknown>;
        return typeof f['format'] === 'string' && typeof f['downloadUrl'] === 'string';
    });
}

/**
 * Runtime narrowing guard for `redirect_to_budget` tool-card payloads.
 */
export function isActionPreviewCardPayload(p: unknown): p is ActionPreviewCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    const obj = p as Record<string, unknown>;
    const preview = obj['preview'];
    if (typeof preview !== 'object' || preview === null) {
        return false;
    }
    const previewObj = preview as Record<string, unknown>;
    if (!Array.isArray(previewObj['sections'])) {
        return false;
    }
    const actions = previewObj['actions'];
    if (typeof actions !== 'object' || actions === null) {
        return false;
    }
    return typeof (obj['proposal_id'] ?? obj['proposalId']) === 'number';
}

export function isBudgetRedirectCardPayload(p: unknown): p is BudgetRedirectCardPayload {
    if (typeof p !== 'object' || p === null) {
        return false;
    }
    // Accept either snake_case (raw core payload) or camelCase (post-middleware)
    // for the `is_draft` / `isDraft` discriminator.
    const obj = p as Record<string, unknown>;
    const isDraft = obj['is_draft'] ?? obj['isDraft'];
    return (
        typeof obj['label'] === 'string' &&
        typeof obj['url'] === 'string' &&
        typeof isDraft === 'boolean'
    );
}

export interface GableChatResponse {
    message: string;
    conversationId: number;
    toolsUsed: string[];
    citations: GableCitation[];
    pendingProposals: GablePendingProposal[];
    /**
     * Structured tool-result cards the UI renders inline. Currently only
     * `generate_report` produces these — payload carries the full report
     * data + download URLs + reports-page deeplink.
     */
    toolCards?: GableToolCard[];
    /**
     * Present iff the AI fired `ask_user_questions` this turn. When set,
     * the UI renders the question card (with `pendingQuestions.leadIn`
     * inside the card) and SUPPRESSES rendering `message` as a standalone
     * assistant bubble — `leadIn` carries the same content for the
     * blocking endpoint, and rendering both would duplicate.
     *
     * For the streaming endpoint the streamed assistant bubble already
     * exists with whatever narration the model produced before firing the
     * tool; that bubble stays visible and the card renders below it.
     */
    pendingQuestions?: GablePendingQuestions | null;
    /**
     * Telemetry event id for this turn — used to wire thumbs-up / down on
     * the assistant message (PATCH /ai-telemetry/events/{id}/feedback). Null
     * (or absent) if telemetry logging failed; thumbs are hidden in that case.
     */
    aiInteractionEventId?: number | null;
    /**
     * AI-suggested follow-up prompts for this turn. Empty array (or absent)
     * means the orchestrator didn't surface any; UI falls back to its
     * hardcoded demoSuggestions in that case.
     */
    followUpSuggestions?: string[];
}

/**
 * Feedback values matching the backend `FeedbackState` enum.
 *  - `'good'` / `'bad'` are the two thumbs states
 *  - `'none'` clears feedback (toggled when re-clicking an active thumb)
 */
export type GableFeedback = 'good' | 'bad' | 'none';

/**
 * Reason-chip selections users (super or otherwise) can attach to a
 * thumbs-down feedback. Lesson-worthy chips (wrong_data / missed_nuance)
 * are the only ones that escalate to the Teach Gable drawer for supers;
 * everything else is telemetry-only.
 */
export type GableFeedbackChip =
    | 'wrong_data'
    | 'missed_nuance'
    | 'wrong_tone'
    | 'off_topic'
    | 'other';

export const LESSON_WORTHY_CHIPS: ReadonlyArray<GableFeedbackChip> = ['wrong_data', 'missed_nuance'];

/**
 * Per-question answer payload the UI sends back. Value type depends on
 * the question's `kind`:
 *   - `single_select` → string (chosen option value, OR free-form text
 *     when the user picked "Something else" on a question with
 *     `allow_other: true`)
 *   - `multi_select`  → string[] (option values, possibly mixed with
 *     free-form text when the user added "Something else")
 *   - `text`          → string
 *   - `number`        → number
 *   - `date`          → string (ISO `yyyy-mm-dd`)
 */
export interface GableQuestionAnswer {
    id: string;
    value: string | string[] | number;
}

export interface GableAnswerQuestionsBody {
    conversationId: number;
    batchId: string;
    answers: GableQuestionAnswer[];
    /**
     * Echo the original questions array back. The backend uses this to
     * map raw option `value`s ("1234") back to their human-readable
     * `label`s ("Lot 4B") when synthesizing the user message the AI
     * sees. Strongly recommended per backend contract — without this
     * the AI sees only opaque ids.
     */
    questionsEcho?: GableQuestion[];
}

/**
 * Handshake response from the chat-stream endpoint. Tells the UI which Echo
 * channel to subscribe to (`private-ai-conversation-{conversationId}`) and which
 * assistant message id is about to start receiving streamed deltas.
 */
export interface GableChatStreamResponse {
    conversationId: number;
    assistantMessageId: number;
}

export interface GablePendingListResponse {
    proposals: GablePendingProposal[];
}

/** One page starter chip. */
export interface GableWelcomeSuggestion {
    label: string;
    prompt: string;
}

/**
 * A group of drawer empty-state suggestions. `kind` is open-ended on
 * purpose: today only 'page_starter', but Gable Autopilot will add proactive
 * groups (review-queue nudges, etc.) here without a breaking change.
 */
export interface GableWelcomeGroup {
    kind: string;
    label: string;
    suggestions: GableWelcomeSuggestion[];
}

export interface GableWelcomeSuggestionsResponse {
    groups: GableWelcomeGroup[];
}

// -----------------------------------------------------------------------------
// SD-6742 — chat history
// -----------------------------------------------------------------------------

export type GableConversationStatus = 'active' | 'archived';

/**
 * One row in the conversation history list (GET /conversations). `title` may
 * be null until the server-side title-generation job names it — the UI falls
 * back to the first user message / "New chat".
 */
export interface GableConversationSummary {
    id: number;
    title: string | null;
    status: GableConversationStatus;
    lastMessageAt: string;
    messageCount: number;
}

/**
 * Pagination envelope for the conversation list (SD-6742 server-side
 * search + paging). `hasMore` is the server's authoritative "another page
 * exists" flag — the UI appends pages until it's false rather than
 * inferring from `total`.
 */
export interface GableConversationListPagination {
    page: number;
    perPage: number;
    total: number;
    hasMore: boolean;
}

export interface GableConversationListResponse {
    conversations: GableConversationSummary[];
    pagination: GableConversationListPagination;
}

/** Params for the conversation list endpoint (server-side search + paging). */
export interface GableListConversationsParams {
    status?: GableConversationStatus;
    /** Trimmed search term; matches title OR message content server-side. */
    search?: string;
    /** 1-based page number (default 1 server-side). */
    page?: number;
    /** Page size (default 25, max 50 server-side). */
    perPage?: number;
}

/** Conversation envelope returned by load / rename / archive endpoints. */
export interface GableConversationDetail {
    id: number;
    title: string | null;
    status: GableConversationStatus;
    lastMessageAt: string;
}

/**
 * One persisted message returned when loading a conversation for resume.
 * Assistant rows carry best-effort reconstructed metadata; user rows carry
 * just role/content/timestamp. Keys are camelCase (response middleware
 * re-cases the top-level envelope; the nested arrays mirror the same shapes
 * the live stream produces).
 */
export interface GableConversationMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    messageId: number;
    interactionId?: number | null;
    streamState?: 'complete' | 'failed';
    citations?: GableCitation[];
    toolCards?: GableToolCard[];
    thoughts?: { id?: string; label: string; ok?: boolean; kind?: 'tool' | 'summary' }[];
    followUpSuggestions?: string[];
}

export interface GableLoadConversationResponse {
    conversation: GableConversationDetail;
    messages: GableConversationMessage[];
}

export interface GableConversationMutationResponse {
    conversation: GableConversationDetail;
}

export interface GableApproveResponse {
    committed: true;
    result: Record<string, unknown>;
    commitLogId: number;
    idempotent?: true;
}

export interface GableRejectResponse {
    rejected: true;
}

/**
 * HTTP client for Gable, PayHOA's AI assistant.
 *
 * Wraps the four endpoints the Gable page uses:
 *  - POST /gable/assistant/organizations/{orgId}/chat
 *  - GET  /gable/assistant/organizations/{orgId}/proposals/pending
 *  - POST /gable/assistant/organizations/{orgId}/proposals/{id}/approve
 *  - POST /gable/assistant/organizations/{orgId}/proposals/{id}/reject
 *
 * Mirrors `AiReportsAssistantService` — thin transport, the page owns
 * conversation/proposal state.
 */
@Injectable({
    providedIn: 'root',
})
export class GableService {
    constructor(
        private _http: ApplicationHttpClient,
        private _pageContext: PageContextService,
    ) {}

    /**
     * Send a chat message. The orchestrator may produce write proposals as a
     * side effect — those come back in `pendingProposals` so the UI can
     * surface the approval panel without a separate fetch.
     */
    public chat(
        message: string,
        conversationId: number | null,
    ): Observable<GableChatResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableChatResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.Chat(jwt.orgId));

        const body: Record<string, unknown> = { message };
        if (conversationId !== null && conversationId !== undefined) {
            body['conversationId'] = conversationId;
        }
        const pageContext = this._pageContext.snapshot();
        if (pageContext) {
            body['pageContext'] = pageContext;
        }

        return this._http.post<GableChatResponse>(url, this._http.serializeObject(body));
    }

    /**
     * Kick off a streaming assistant turn. Returns a handshake (conversation
     * id + assistant message id) immediately — the actual response text is
     * delivered as `ai.stream.*` events on the
     * `private-ai-conversation-{conversationId}` Echo channel (lowercased,
     * hyphenated — uppercase/dotted names have bitten us before).
     *
     * Callers should subscribe to that channel as soon as this resolves and
     * append text-delta payloads to the placeholder bubble keyed on
     * `assistantMessageId`. The blocking `chat()` method is preserved as a
     * fallback for callers (and tests) that don't want streaming.
     */
    public chatStream(
        message: string,
        conversationId: number | null,
    ): Observable<GableChatStreamResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableChatStreamResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.ChatStream(jwt.orgId));

        const body: Record<string, unknown> = { message };
        if (conversationId !== null && conversationId !== undefined) {
            body['conversationId'] = conversationId;
        }
        const pageContext = this._pageContext.snapshot();
        if (pageContext) {
            body['pageContext'] = pageContext;
        }

        return this._http.post<GableChatStreamResponse>(url, this._http.serializeObject(body));
    }

    /**
     * Page-aware starter suggestions for the drawer empty state. Keyed to the
     * current route (Haiku on the server, cached). Returns a flexible group
     * envelope — today just page starters, later also Autopilot proactive
     * items — so the drawer can render new group kinds without a change here.
     * Best-effort: callers should treat any error as "no groups".
     */
    public welcomeSuggestions(): Observable<GableWelcomeSuggestionsResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return of({ groups: [] });
        }
        const pageContext = this._pageContext.snapshot();
        if (!pageContext?.url) {
            return of({ groups: [] });
        }
        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.WelcomeSuggestions(jwt.orgId));
        let params = new HttpParams().set('url', pageContext.url);
        if (pageContext.title) {
            params = params.set('title', pageContext.title);
        }
        if (pageContext.componentName) {
            params = params.set('componentName', pageContext.componentName);
        }
        return this._http
            .get<GableWelcomeSuggestionsResponse>(url, { params })
            .pipe(catchError(() => of({ groups: [] })));
    }

    /**
     * Fetch all proposals still pending for this user/org. Used to backfill
     * the approval panel on first page load (before any chat round-trip).
     */
    public listPendingProposals(): Observable<GablePendingListResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GablePendingListResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.PendingProposals(jwt.orgId));

        return this._http.get<GablePendingListResponse>(url);
    }

    /**
     * Decided-proposal history (approved / rejected / expired) for the
     * Activity tab feed. `actionTypes` narrows to a module's action set.
     */
    public getProposalActivity(
            actionTypes?: string[],
            page: number = 1,
            perPage: number = 20,
    ): Observable<GableActivityFeedResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableActivityFeedResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.ProposalActivity(jwt.orgId));
        let params = new HttpParams()
                .set('page', String(page))
                .set('perPage', String(perPage));
        if (actionTypes?.length) {
            params = params.set('actionTypes', actionTypes.join(','));
        }

        return this._http.get<GableActivityFeedResponse>(url, { params });
    }

    /** Per-action-type proposal counts for the hero's module quick links. */
    public getActivitySummary(): Observable<{ items: GableActivitySummaryItem[] }> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<{ items: GableActivitySummaryItem[] }>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.GableModule.ProposalActivitySummary(jwt.orgId));

        return this._http.get<{ items: GableActivitySummaryItem[] }>(url);
    }

    // -------------------------------------------------------------------------
    // SD-6742 — chat history (list / load / rename / archive)
    // -------------------------------------------------------------------------

    /**
     * List the current user's conversations for this org, ordered by
     * last_message_at DESC. `status` filters active vs archived (defaults to
     * active server-side; we pass it explicitly so the two lists are
     * unambiguous). `search` runs a server-side match over BOTH the title and
     * message content; `page`/`perPage` drive pagination — the response
     * carries a `pagination` envelope (`hasMore`) so callers can append pages.
     */
    public listConversations(
        params: GableListConversationsParams = {},
    ): Observable<GableConversationListResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationListResponse>;
        }

        const query: string[] = [`status=${params.status ?? 'active'}`];
        const search = (params.search ?? '').trim();
        if (search !== '') {
            query.push(`search=${encodeURIComponent(search)}`);
        }
        if (params.page !== undefined) {
            query.push(`page=${params.page}`);
        }
        if (params.perPage !== undefined) {
            query.push(`perPage=${params.perPage}`);
        }

        const url = Routes.MakeLegFiCoreUrl(
            `${Routes.GableModule.Conversations(jwt.orgId)}?${query.join('&')}`,
        );

        return this._http.get<GableConversationListResponse>(url);
    }

    /**
     * Load one conversation (header + full message history) for display +
     * resume. The returned `conversationId` is what callers pass to
     * `chatStream` to continue the thread.
     */
    public loadConversation(
        conversationId: number,
    ): Observable<GableLoadConversationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableLoadConversationResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.Conversation(jwt.orgId, conversationId),
        );

        return this._http.get<GableLoadConversationResponse>(url);
    }

    /** Rename a conversation (title <= 255 chars). */
    public renameConversation(
        conversationId: number,
        title: string,
    ): Observable<GableConversationMutationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationMutationResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.Conversation(jwt.orgId, conversationId),
        );

        return this._http.patch<GableConversationMutationResponse>(
            url,
            this._http.serializeObject({ title }),
        );
    }

    /** Archive a conversation (active -> archived). */
    public archiveConversation(
        conversationId: number,
    ): Observable<GableConversationMutationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationMutationResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.ArchiveConversation(jwt.orgId, conversationId),
        );

        return this._http.post<GableConversationMutationResponse>(url, this._http.serializeObject({}));
    }

    /** Unarchive a conversation (archived -> active). */
    public unarchiveConversation(
        conversationId: number,
    ): Observable<GableConversationMutationResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableConversationMutationResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.UnarchiveConversation(jwt.orgId, conversationId),
        );

        return this._http.post<GableConversationMutationResponse>(url, this._http.serializeObject({}));
    }

    /**
     * Commit a pending proposal. The caller generates an idempotency key
     * (UUID v4) so a double-click or retry replays the prior result instead
     * of double-writing.
     */
    public approveProposal(
        proposalId: number,
        idempotencyKey: string,
    ): Observable<GableApproveResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableApproveResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.ApproveProposal(jwt.orgId, proposalId),
        );

        return this._http.post<GableApproveResponse>(
            url,
            this._http.serializeObject({ idempotencyKey }),
        );
    }

    /**
     * Submit answers for an open question batch. Server response uses the
     * same envelope as `chat()` — typically the AI's next turn (which may
     * itself contain a new `pendingQuestions` for a follow-up batch, or
     * a `pendingProposals` entry once the AI has enough info).
     */
    public answerQuestions(
        body: GableAnswerQuestionsBody,
    ): Observable<GableChatResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableChatResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.AnswerQuestions(jwt.orgId),
        );

        return this._http.post<GableChatResponse>(url, this._http.serializeObject(body));
    }

    /**
     * Record a thumbs-up / thumbs-down on an assistant turn. The
     * `aiInteractionEventId` lives on the chat response (and on the
     * `ai.stream.completed` event for streaming). Backend updates the
     * `feedback` + `feedback_at` + `feedback_user_id` columns on the
     * matching `AiInteractionEvent` row.
     */
    public recordFeedback(
        eventId: number,
        feedback: GableFeedback,
    ): Observable<unknown> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<unknown>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.AiTelemetryModule.RecordFeedback(eventId));

        return this._http.patch<unknown>(url, this._http.serializeObject({ feedback }));
    }

    /**
     * Record a reason-chip selection (plus optional free text) on an
     * assistant turn. Telemetry-only; the Teach Gable lesson flow is a
     * separate path (super-only + lesson-worthy chip). Powers the
     * inline feedback panel under each bubble.
     */
    public recordChipFeedback(
        eventId: number,
        chip: GableFeedbackChip,
        detail?: string,
    ): Observable<unknown> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<unknown>;
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.AiTelemetryModule.RecordChipFeedback(eventId));
        const body: { chip: string; detail?: string } = { chip };
        if (detail) { body.detail = detail; }

        return this._http.patch<unknown>(url, this._http.serializeObject(body));
    }

    /**
     * Mark a pending proposal as rejected. No payload required.
     */
    /**
     * Fetch the persisted toolCards for one assistant message — UI fallback
     * for when Pusher dropped them from the stream-completed broadcast
     * (full report payloads routinely exceed Pusher's ~10KB event limit).
     */
    public fetchToolCards(
        conversationId: number,
        messageId: number,
    ): Observable<{ toolCards: GableToolCard[] }> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<{ toolCards: GableToolCard[] }>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.ToolCards(jwt.orgId, conversationId, messageId),
        );

        return this._http.get<{ toolCards: GableToolCard[] }>(url);
    }

    /**
     * Confirm + execute an action-preview proposal. The framework's
     * twin of `approveProposal()` — distinct path because action-preview
     * proposals route through {@see \App\Modules\AIGable\App\Actions\ActionDefinition}
     * rather than the generic Eloquent CRUD allowlist.
     */
    public commitActionProposal(
        proposalId: number,
        idempotencyKey: string,
        edits: ActionPreviewEdits,
        // Optional chat context — when present, core runs the post-commit
        // follow-ups job so "what next" chips appear under the card's message.
        chatContext?: { conversationId: number; messageId: number },
    ): Observable<ActionPreviewCommitResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<ActionPreviewCommitResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.CommitAction(jwt.orgId, proposalId),
        );

        return this._http.post<ActionPreviewCommitResponse>(
            url,
            this._http.serializeObject({
                idempotencyKey,
                edits,
                ...(chatContext ? { conversationId: chatContext.conversationId, messageId: chatContext.messageId } : {}),
            }),
        );
    }

    /**
     * Refine an in-flight action-preview proposal — utility-action
     * clicks ("Soften tone", "Restore skipped") and recipient/text
     * edits land here. Server reruns ActionDefinition::refine() and
     * returns the new section bundle the card swaps in.
     */
    public refineActionProposal(
        proposalId: number,
        utilityActionId: string,
        edits: ActionPreviewEdits,
    ): Observable<ActionPreviewRefineResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<ActionPreviewRefineResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.RefineProposal(jwt.orgId, proposalId),
        );

        return this._http.post<ActionPreviewRefineResponse>(
            url,
            this._http.serializeObject({ utilityActionId, edits }),
        );
    }

    /**
     * Cancel an in-flight streaming assistant turn (Claude-desktop-style
     * Stop button). Server marks the message as cancelled + writes a
     * cache flag the streaming job polls; on the next Prism callback the
     * job short-circuits and stops emitting deltas. The UI calls this
     * eagerly when the user clicks Stop; whatever has streamed so far
     * stays in the bubble.
     */
    public cancelStream(messageId: number): Observable<{ cancelled: true }> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<{ cancelled: true }>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.CancelStream(jwt.orgId, messageId),
        );

        return this._http.post<{ cancelled: true }>(url, this._http.serializeObject({}));
    }

    public rejectProposal(proposalId: number): Observable<GableRejectResponse> {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable() as Observable<GableRejectResponse>;
        }

        const url = Routes.MakeLegFiCoreUrl(
            Routes.GableModule.RejectProposal(jwt.orgId, proposalId),
        );

        return this._http.post<GableRejectResponse>(url, this._http.serializeObject({}));
    }
}
